/**
 * Turf.js Utilities
 *
 * Re-exports and wrapper functions for common spatial operations.
 * These are used for client-side geometry processing.
 */

// Re-export commonly used Turf functions
export {
  // Measurement
  distance,
  area,
  length,
  bbox,
  center,
  centroid,
  // Boolean operations
  booleanContains,
  booleanIntersects,
  booleanPointInPolygon,
  booleanValid,
  // Transformation
  buffer,
  simplify,
  difference,
  transformTranslate,
  // Helpers
  point,
  polygon,
  lineString,
  featureCollection,
  // Misc
  nearestPoint,
} from "@turf/turf";

import {
  point,
  distance,
  featureCollection,
} from "@turf/turf";
import type { Point, FeatureCollection } from "geojson";

/**
 * Calculate the distance between two points in meters
 */
export function getDistanceMeters(
  from: { x: number; y: number },
  to: { x: number; y: number }
): number {
  const fromPoint = point([from.x, from.y]);
  const toPoint = point([to.x, to.y]);
  return distance(fromPoint, toPoint, { units: "meters" });
}

/**
 * Find the N nearest POIs to a given location
 */
export function findNearestPOIs<T extends { location: { x: number; y: number } }>(
  userLocation: { x: number; y: number },
  pois: T[],
  limit: number = 5
): Array<T & { distance: number }> {
  const userPoint = point([userLocation.x, userLocation.y]);

  const withDistances = pois.map((poi) => ({
    ...poi,
    distance: distance(
      userPoint,
      point([poi.location.x, poi.location.y]),
      { units: "meters" }
    ),
  }));

  return withDistances
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
}

/**
 * Create a GeoJSON FeatureCollection from POIs
 */
export function poisToFeatureCollection<T extends {
  id: string;
  name: string;
  location: { x: number; y: number } | null;
}>(
  pois: T[]
): FeatureCollection<Point> {
  const features = pois
    .filter((poi) => poi.location !== null)
    .map((poi) => ({
      type: "Feature" as const,
      properties: {
        id: poi.id,
        name: poi.name,
      },
      geometry: {
        type: "Point" as const,
        coordinates: [poi.location!.x, poi.location!.y],
      },
    }));

  return featureCollection(features);
}
